import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';

import classNames from 'classnames';

import { Livestream, Media, User } from '../../../types/__generated__';
import clientConfig from '../../client/config';
import { PAGES_UI_FEATURE_IMAGE } from '../../constants/imageTemplateStrings';
import PageType from '../../constants/pageTypes';
import { PageMetaContext } from '../../context';
import { useDonationAmount, useNotNullableContext } from '../../hooks';
import useBasePageQuery from '../../hooks/useBasePageQuery';
import { useFlags } from '../../hooks/useFlags';
import { useModalToggle } from '../../hooks/useModalToggle';
import getStreamDetails from '../../utils/getStreamDetails';
import { onlyImages } from '../../utils/typeGuards';
import { LazyLoader } from '../LazyLoader/LazyLoader';
import StreamChat from '../StreamChat/StreamChat';
import ImageButtons from './ImageButtons/ImageButtons';

import './FeatureImage.scss';
import styles from './FeatureImage.scss';
import classnames from 'classnames';

const LightboxLazy = React.lazy(() => import('../Lightbox/Lightbox'));

interface FeatureImageProps {
  enableLightbox?: boolean;
}

type ImageDetails = {
  url: string;
  caption?: string;
};

export const FeatureImage: FunctionComponent<FeatureImageProps> = ({
  enableLightbox = false,
}) => {
  const { t } = useTranslation('common', { useSuspense: false });
  const [videoRef, inViewRef] = useInView();
  const { enableOtgVideoChat } = useFlags();
  const { pageType } = useNotNullableContext(PageMetaContext);

  const { ref, setOpen, isOpen, Portal } = useModalToggle();

  // AB test: starting_donation_on_frp_with_amount_page
  // Can be removed when experiment has ended
  const { showDonationAmountVariant } = useDonationAmount();
  const imageHeightStyle =
    showDonationAmountVariant === 'B'
      ? styles.image_height_tall
      : styles.image_height_regular;

  const basePageData = useBasePageQuery();

  if (!basePageData.data?.page) {
    return null;
  }

  const {
    hero,
    heroMedia,
    mediaCollection,
    legacyId,
    livestream,
    owner,
  } = basePageData.data.page;

  const DEFAULT_IMAGE =
    pageType === PageType.ONE_PAGE
      ? `${clientConfig.imageServiceUrl}page-balloons.jpg`
      : `${clientConfig.imageServiceUrl}trainer-no-branding_web.jpg`;

  const heroImageUrl = (hero?.imageUrl ||
    (heroMedia?.__typename === 'ImageMedia' && heroMedia?.url) ||
    DEFAULT_IMAGE) as string;
  const altText =
    hero?.imageTitle ||
    (heroMedia?.__typename === 'ImageMedia' && heroMedia.alt) ||
    '';

  const galleryImages: ImageDetails[] = [{ url: heroImageUrl }];

  const mediaCollectionUrls: ImageDetails[] =
    mediaCollection?.nodes.filter(onlyImages).map(image => {
      return { url: image.url, caption: image.caption ?? undefined };
    }) ?? [];

  galleryImages.push(...mediaCollectionUrls);

  if (basePageData.loading) {
    return null;
  }

  if (legacyId) {
    const streamDetails = getStreamDetails(
      livestream as Livestream | null | undefined,
      owner as User | null | undefined,
      heroMedia as Media,
    );

    if (streamDetails?.streamUrl) {
      return (
        <div
          ref={videoRef}
          className={classNames(!inViewRef ? styles.videoPopout : '')}
        >
          <iframe
            title={`${streamDetails.title}`}
            src={`${streamDetails.streamUrl}`}
            height="400"
            width="100%"
            frameBorder="0"
            scrolling="no"
            allowFullScreen
            className={styles.stream}
            data-qa="feature-image-stream"
          />
          {enableOtgVideoChat && inViewRef && <StreamChat {...streamDetails} />}
        </div>
      );
    }
  }

  return (
    <>
      <div className={styles.imageWrapper}>
        <img
          className={classnames(styles.image, imageHeightStyle)}
          src={`${heroImageUrl}?template=${PAGES_UI_FEATURE_IMAGE}`}
          alt={altText}
          data-qa="feature-image"
        />
        {enableLightbox && (
          <button
            data-qa="feature-image-overlay"
            onClick={() => setOpen(true)}
            type="button"
            className={styles.overlayButton}
            tabIndex={-1}
          >
            <span className="jg-sr-only">
              {t('common.openGallery', 'Open the Image Gallery')}
            </span>
          </button>
        )}
        {enableLightbox && <ImageButtons onViewGallery={() => setOpen(true)} />}
      </div>
      {isOpen && (
        <Portal scrollLock focusLock background>
          <LazyLoader>
            <LightboxLazy
              ref={ref}
              onClose={() => setOpen(false)}
              images={galleryImages.map(image => ({
                original: image.url,
                thumbnail: image.url,
                description: image.caption,
              }))}
            />
          </LazyLoader>
        </Portal>
      )}
    </>
  );
};

export default FeatureImage;
